import {
    AddButton,
    BackButton,
    CancelButton,
    ConfirmButton,
    ContinueButton,
    DeleteButton,
    DoneButton,
    DownloadButton,
    EditButton,
    NextButton,
    RetryButton,
    SaveButton,
    SendButton,
    SkipButton,
    StopButton,
    SubmitButton,
} from "@patient-watch/forms";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { useNav } from "@/utils/nav";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useRef, useState, type MouseEvent } from "react";

export const Route = createFileRoute("/_app/design/components/buttons")({
    component: ButtonAnimationsDemo,
});

type DemoButtonKey =
    | "submit"
    | "next"
    | "save"
    | "send"
    | "skip"
    | "back"
    | "cancel"
    | "delete"
    | "continue"
    | "confirm"
    | "add"
    | "edit"
    | "done"
    | "stop"
    | "retry"
    | "download";

const DEMO_BUTTONS: { key: DemoButtonKey; title: string; Component: typeof SubmitButton }[] = [
    { key: "submit", title: "Submit", Component: SubmitButton },
    { key: "next", title: "Next", Component: NextButton },
    { key: "continue", title: "Continue", Component: ContinueButton },
    { key: "confirm", title: "Confirm", Component: ConfirmButton },
    { key: "save", title: "Save", Component: SaveButton },
    { key: "send", title: "Send", Component: SendButton },
    { key: "add", title: "Add", Component: AddButton },
    { key: "edit", title: "Edit", Component: EditButton },
    { key: "done", title: "Done", Component: DoneButton },
    { key: "skip", title: "Skip", Component: SkipButton },
    { key: "back", title: "Back", Component: BackButton },
    { key: "retry", title: "Retry", Component: RetryButton },
    { key: "download", title: "Download", Component: DownloadButton },
    { key: "cancel", title: "Cancel", Component: CancelButton },
    { key: "stop", title: "Stop", Component: StopButton },
    { key: "delete", title: "Delete", Component: DeleteButton },
];

function ButtonAnimationsDemo() {
    useNav([
        { label: "Design", route: { to: "/design" } },
        { label: "Buttons", route: { to: "/design/components/buttons" } },
    ]);

    const [loading, setLoading] = useState<Record<DemoButtonKey, boolean>>({
        submit: false,
        next: false,
        save: false,
        send: false,
        skip: false,
        back: false,
        cancel: false,
        delete: false,
        continue: false,
        confirm: false,
        add: false,
        edit: false,
        done: false,
        stop: false,
        retry: false,
        download: false,
    });
    const timeoutsRef = useRef<Partial<Record<DemoButtonKey, ReturnType<typeof setTimeout>>>>({});

    const simulateLoading = useCallback((key: DemoButtonKey) => {
        const existingTimeout = timeoutsRef.current[key];
        if (existingTimeout) clearTimeout(existingTimeout);

        setLoading((current) => ({ ...current, [key]: true }));

        timeoutsRef.current[key] = setTimeout(() => {
            setLoading((current) => ({ ...current, [key]: false }));
            delete timeoutsRef.current[key];
        }, 500);
    }, []);

    const handleClick = useCallback(
        (key: DemoButtonKey) => (event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            simulateLoading(key);
        },
        [simulateLoading]
    );

    return (
        <AdminPageLayout
            title="Action buttons"
            description="Hover to see icon animations. Click any button to show loading for 500ms."
            maxWidthClassName="max-w-2xl"
        >
            <div className="space-y-8">
                <div className="space-y-6">
                    {DEMO_BUTTONS.map(({ key, title, Component }) => (
                        <div key={key} className="space-y-2">
                            <h2 className="font-semibold text-xl">{title}</h2>
                            <Component isLoading={loading[key]} onClick={handleClick(key)} />
                        </div>
                    ))}
                </div>

                <div className="bg-muted p-4 rounded-lg">
                    <h3 className="mb-2 font-semibold">Animation details</h3>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                        <li>Submit / Confirm / Done: check icon rotates 6 degrees anticlockwise on hover</li>
                        <li>Next / Continue / Edit: icon nudges right on hover</li>
                        <li>Back: arrow on the left nudges left on hover (mirrors Next)</li>
                        <li>Save / Cancel / Add / Delete / Stop: icon rotates 10 degrees clockwise on hover</li>
                        <li>Send: send icon nudges up and right on hover</li>
                        <li>Skip: flipped undo icon rotates 15 degrees anticlockwise on hover</li>
                        <li>Retry: rotate icon spins 15 degrees anticlockwise on hover</li>
                        <li>Download: download icon nudges down on hover</li>
                        <li>Click any button to preview its loading state</li>
                    </ul>
                </div>
            </div>
        </AdminPageLayout>
    );
}
